import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import * as api from './api.ts';
import { SignIn } from './screens.tsx';
import { Button, Field, Finding, GlassPanel, Spinner } from './ui.tsx';
import { knownRegion, phonePlaceholder } from './lib/phone-regions.ts';
import { RegionPicker, WorkMobileVerify, workMobileHelp } from './work-mobile.tsx';

/**
 * The invitation page (2026-09-30) — where the Accept and No-thanks links in
 * an invitation email land (`/invitations/<token>`, `/invitations/<token>/decline`).
 *
 * Until now those links pointed at the API origin, where only POST routes
 * existed, so every click 404'd and nobody could join an organisation by
 * invitation. The page reads what the invitation is for, declines in one tap
 * with no sign-in (the first message must be declinable without enrolling),
 * signs the invitee in to accept, and collects the work mobile the role owes
 * (migration 0138) — confirming it by a texted code straight after joining,
 * because a code cannot be texted before the person belongs to anything.
 */

const ROLE_WORDS: Record<string, string> = {
  owner: 'the owner',
  admin: 'an administrator',
  viewer: 'a viewer',
  staff: 'staff',
  occupant: 'a member',
};

export function parseInvitationPath(pathname: string): { token: string; decline: boolean } | null {
  const m = /^\/invitations\/([^/]+)(\/decline)?\/?$/.exec(pathname);
  if (!m) return null;
  return { token: decodeURIComponent(m[1]!), decline: Boolean(m[2]) };
}

export function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="first-light-ground flex min-h-screen items-center justify-center px-6 py-12">
      <div className="horizon-rule" aria-hidden="true" />
      <GlassPanel className="w-full max-w-[440px] px-8 py-9">
        <p className="text-[12px] leading-[16px] font-[650] tracking-[0.06em] text-[var(--ink-3)] uppercase">
          EVAC
        </p>
        <h1 className="mt-3 mb-4 text-[28px] leading-[32px] font-[650] tracking-[0.196px] text-[var(--ink)]">
          {title}
        </h1>
        {children}
      </GlassPanel>
    </main>
  );
}

export function OpenDashboard() {
  return (
    <Button variant="primary" size="auth" full onClick={() => window.location.assign('/home')}>
      Open the dashboard
    </Button>
  );
}

export function InvitationPage({
  token,
  decline,
  signedIn,
  onSignedIn,
}: {
  token: string;
  decline: boolean;
  signedIn: boolean;
  onSignedIn: () => void;
}) {
  const qc = useQueryClient();
  const [wantsToAccept, setWantsToAccept] = useState(false);
  const [mobile, setMobile] = useState('');
  /** The number's own country; the organisation's until the person picks another. */
  const [mobileRegion, setMobileRegion] = useState<string | null>(null);
  const [skipVerify, setSkipVerify] = useState(false);
  /** What this organisation will call them (0166); empty = the name they go by. */
  const [workName, setWorkName] = useState('');
  /** The code emailed to the invited address, when accepting on another account. */
  const [linkCode, setLinkCode] = useState('');

  const invitation = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.getInvitation(token),
    retry: false,
  });
  const me = useQuery({ queryKey: ['me'], queryFn: api.getMe, enabled: signedIn });

  const declined = useMutation({ mutationFn: () => api.declineInvitation(token) });
  const accepted = useMutation({
    mutationFn: () =>
      api.acceptInvitation(
        token,
        mobile.trim() || undefined,
        mobileRegion ?? invitation.data?.countryCode ?? undefined,
        {
          ...(workName.trim() ? { workName: workName.trim() } : {}),
          ...(linkCode.trim() ? { linkCode: linkCode.trim() } : {}),
        },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });
  // Signed in to a different account than the one invited (0166): the page
  // offers to add this job to THAT account, after a code to the invited
  // mailbox proves the person reads it. Their personal details stay private.
  const otherAccount =
    accepted.error instanceof api.ApiError &&
    accepted.error.status === 403 &&
    accepted.error.detail.canLink === true;
  const sendLink = useMutation({ mutationFn: () => api.startInvitationLink(token) });

  if (declined.isSuccess) {
    return (
      <Page title="Declined">
        <p className="text-[17px] leading-[23px] text-[var(--ink-2)]">
          You won't be added. If that was a mistake, ask whoever invited you to send a new
          invitation.
        </p>
      </Page>
    );
  }

  if (invitation.isPending) return <Spinner />;

  if (invitation.isError || !invitation.data) {
    const gone = invitation.error instanceof api.ApiError && invitation.error.status === 404;
    return (
      <Page
        title={gone ? 'This invitation is no longer valid' : 'The invitation could not be read'}
      >
        <p className="mb-6 text-[17px] leading-[23px] text-[var(--ink-2)]">
          {gone
            ? 'It may have expired, already been used, or been withdrawn. Ask whoever invited you to send a new one.'
            : 'This is a connection problem, not a problem with the invitation. Try again in a moment.'}
        </p>
        {gone ? null : (
          <Button variant="primary" size="auth" full onClick={() => void invitation.refetch()}>
            Try again
          </Button>
        )}
      </Page>
    );
  }

  const inv = invitation.data;
  const asRole = ROLE_WORDS[inv.role] ?? inv.role;

  // Accepted: confirm the work mobile by code if the role owes one and a code
  // can be sent here; otherwise straight to the dashboard (its banner asks).
  if (accepted.isSuccess && accepted.data) {
    const joined = accepted.data;
    const canVerify = me.data?.workMobile?.verifyAvailable !== false;
    const owesVerify =
      inv.requiresWorkMobile && canVerify && joined.workMobileVerified === false && !skipVerify;
    return (
      <Page title={`You've joined ${inv.organisation}`}>
        {owesVerify ? (
          <>
            <p className="mb-4 text-[17px] leading-[23px] text-[var(--ink-2)]">
              One last thing: confirm your mobile with the code we text to it.
            </p>
            <WorkMobileVerify
              countryCode={mobileRegion ?? inv.countryCode}
              orgIds={[joined.orgId]}
              role={inv.role}
              initialPhone={mobile}
              onDone={() => setSkipVerify(true)}
            />
            <div className="mt-4">
              <Button variant="quiet" onClick={() => setSkipVerify(true)}>
                Do this later
              </Button>
            </div>
          </>
        ) : (
          <OpenDashboard />
        )}
      </Page>
    );
  }

  // Signed out and choosing to accept: sign in as the invited address. The URL
  // stays on this page, so signing in lands straight back here.
  if (wantsToAccept && !signedIn) {
    return <SignIn onSignedIn={onSignedIn} />;
  }

  const mobileMissing = inv.requiresWorkMobile && !mobile.trim();

  return (
    <Page title={`Join ${inv.organisation}`}>
      <p className="mb-6 text-[17px] leading-[23px] text-[var(--ink-2)]">
        {inv.organisation} would like to add you to their emergency roster as {asRole}. You would be
        alerted if they run a drill or declare an emergency, and could answer that you are safe.
      </p>

      {decline && !wantsToAccept ? (
        <div className="space-y-3">
          <Button
            variant="primary"
            size="auth"
            full
            disabled={declined.isPending}
            onClick={() => declined.mutate()}
          >
            {declined.isPending ? 'Declining…' : 'No thanks'}
          </Button>
          {declined.isError ? <Finding>{api.errorMessage(declined.error)}</Finding> : null}
          <Button variant="quiet" size="auth" full onClick={() => setWantsToAccept(true)}>
            I'd like to join instead
          </Button>
        </div>
      ) : signedIn || wantsToAccept ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (mobileMissing || accepted.isPending) return;
            accepted.mutate();
          }}
        >
          <RegionPicker
            value={mobileRegion ?? knownRegion(inv.countryCode)}
            onChange={setMobileRegion}
          />
          <Field
            label={`Your name at ${inv.organisation} (optional)`}
            value={workName}
            onChange={setWorkName}
            autoComplete="name"
            hint={`Only ${inv.organisation} sees this. Leave it empty to use the name you go by.`}
          />
          <Field
            label={inv.requiresWorkMobile ? 'Your mobile number' : 'Your mobile number (optional)'}
            value={mobile}
            onChange={setMobile}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={phonePlaceholder(mobileRegion ?? inv.countryCode)}
            hint={workMobileHelp(inv.role)}
          />
          <Button
            type="submit"
            variant="primary"
            size="auth"
            full
            disabled={mobileMissing || accepted.isPending}
          >
            {accepted.isPending ? 'Joining…' : `Join ${inv.organisation}`}
          </Button>
          {mobileMissing ? (
            <p className="text-[13px] text-[var(--ink-2)]">Add your mobile number to join.</p>
          ) : null}
          {otherAccount || sendLink.isSuccess ? (
            <div className="space-y-3 rounded-[var(--radius-2)] border border-[var(--line)] p-4">
              <p className="text-[15px] leading-[21px] text-[var(--ink)]">
                This invitation went to a different email address from the one you are signed in
                with. You can add this job to the account you are using now: we email a code to the
                invited address, and you type it here.
              </p>
              {sendLink.isSuccess ? (
                <Field
                  label="Code from the invited address"
                  value={linkCode}
                  onChange={setLinkCode}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  hint={`${inv.organisation} will know you by the invited address. Your personal details stay private to you.`}
                />
              ) : (
                <Button
                  variant="primary"
                  full
                  disabled={sendLink.isPending}
                  onClick={() => sendLink.mutate()}
                >
                  {sendLink.isPending ? 'Sending…' : 'Email a code to the invited address'}
                </Button>
              )}
              {sendLink.isError ? <Finding>{api.errorMessage(sendLink.error)}</Finding> : null}
            </div>
          ) : null}
          {accepted.isError && !otherAccount ? (
            <Finding>{api.errorMessage(accepted.error)}</Finding>
          ) : null}
          <Button
            variant="quiet"
            size="auth"
            full
            disabled={declined.isPending}
            onClick={() => declined.mutate()}
          >
            No thanks
          </Button>
        </form>
      ) : (
        <div className="space-y-3">
          <Button variant="primary" size="auth" full onClick={() => setWantsToAccept(true)}>
            Accept
          </Button>
          <Button
            variant="quiet"
            size="auth"
            full
            disabled={declined.isPending}
            onClick={() => declined.mutate()}
          >
            {declined.isPending ? 'Declining…' : 'No thanks'}
          </Button>
          {declined.isError ? <Finding>{api.errorMessage(declined.error)}</Finding> : null}
        </div>
      )}
    </Page>
  );
}
